/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  ModalProps,
  Row,
  Select
} from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'

import {
  useClassifierServiceGetInvestigationClassifierEndpoint,
  useClassifierServiceGetInvestigationClassifierListEndpoint,
  useInvestigationsServiceCreateInvestigationTestEndpoint,
  useInvestigationsServiceCreateInvestigationTestResultEndpoint,
  useInvestigationsServiceGetInvestigationTestsEndpointKey,
  useInvestigationsServiceGetListSpecimenInvestigationsEndpointKey,
  useInvestigationsServiceGetSupplyTemplateListEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  CustomItemRender,
  message,
  TestTypesInfiniteSelect,
  Text,
  Upload
} from 'src/components'
import { useSessionContext } from 'src/contexts'
import { RECIPE_MEDICINE_FORM } from 'src/features/persons/utils/constants'
import { getFullName } from 'src/features/users/utils'
import { useFileHandler, useUser } from 'src/hooks'
import {
  dayMonthYearHourMinutesFormatWithDot,
  disabledFutureDate
} from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from './styles'
import { TestConsumablesTables } from './TestConsumablesTables'

type Props = {
  onCancel: () => void
  investigationId: string
  typeId?: string
  /** Specimen type, so only investigation types accepting it are offered. */
  specimenTypeId?: string
  /** False once a test of the investigation has results: the type is fixed. */
  canChangeInvestigationType?: boolean
} & ModalProps

export const AddTestResultModal = ({
  onCancel,
  investigationId,
  typeId,
  specimenTypeId,
  canChangeInvestigationType = true,
  ...props
}: Props) => {
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()

  const queryClient = useQueryClient()
  const { userData } = useUser()
  const { session } = useSessionContext()

  const [form] = Form.useForm()
  const [createdTestId, setCreatedTestId] = useState<string>()
  const [isSaving, setIsSaving] = useState(false)

  const testTypeId = Form.useWatch('typeId', form)
  // The laboratory picks the investigation type (the demand's type by default);
  // the test types and result rows follow it.
  const selectedTypeId: string | undefined =
    Form.useWatch('investigationTypeId', form) ?? typeId
  const allValues = Form.useWatch([], form)

  const { onFileChange, isUploading, saveFiles } = useFileHandler({
    form,
    folder: `results`
  })

  const { data: template, isLoading: isLoadingTemplate } =
    useInvestigationsServiceGetSupplyTemplateListEndpoint(
      { typeId: String(testTypeId) },
      [testTypeId],
      { enabled: !!testTypeId }
    )

  const { data: investigation, isLoading } =
    useClassifierServiceGetInvestigationClassifierEndpoint(
      { id: String(selectedTypeId) },
      [selectedTypeId],
      { enabled: !!selectedTypeId }
    )

  const { data: demandInvestigationType } =
    useClassifierServiceGetInvestigationClassifierEndpoint(
      { id: String(typeId) },
      [typeId],
      { enabled: !!typeId }
    )

  // Alternatives: active types of the same category that accept the specimen.
  const categoryId = demandInvestigationType?.category?.id

  const { data: investigationTypes } =
    useClassifierServiceGetInvestigationClassifierListEndpoint(
      {
        pageNumber: 1,
        pageSize: 100,
        isActive: true,
        categoryIds: categoryId ? [categoryId] : undefined,
        specimenTypeIds: specimenTypeId ? [specimenTypeId] : undefined
      },
      undefined,
      { enabled: !!categoryId && canChangeInvestigationType }
    )

  const investigationTypeOptions = [
    ...(investigationTypes?.data ?? []).map((item) => ({
      value: item.id,
      label: item.title?.[currentLang?.value]
    })),
    ...(typeId && !investigationTypes?.data?.some((item) => item.id === typeId)
      ? [
          {
            value: typeId,
            label: demandInvestigationType?.title?.[currentLang?.value]
          }
        ]
      : [])
  ]

  // Result rows only for the chosen test type (all of them until one is chosen).
  const resultTestTypes = (investigation?.testTypes ?? []).filter(
    (item) => !testTypeId || item.id === testTypeId
  )

  // Category, investigation type and work method describe the investigation the
  // test belongs to; they are fixed by the classifier, so they only get shown.
  const investigationDetails = [
    {
      label: t('classifiers.category'),
      value: investigation?.category?.translation?.[currentLang?.value]
    },
    {
      label: t('laboratory_classifiers.work_method'),
      value: investigation?.workMethod?.translation?.[currentLang?.value]
    }
  ].filter((item) => !!item.value)

  const { mutateAsync: createTest } =
    useInvestigationsServiceCreateInvestigationTestEndpoint()

  const { mutateAsync: createResult } =
    useInvestigationsServiceCreateInvestigationTestResultEndpoint()

  const invalidateTests = () =>
    [
      useInvestigationsServiceGetInvestigationTestsEndpointKey,
      // The investigation type shown on the specimen may have changed.
      useInvestigationsServiceGetListSpecimenInvestigationsEndpointKey
    ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))

  const buildTestPayload = (values: AnyObject) => ({
    investigationId,
    investigationTypeId:
      values.investigationTypeId && values.investigationTypeId !== typeId
        ? values.investigationTypeId
        : undefined,
    testedAt: dayjs(values.testedAt).format(),
    supplies: (values.recipesMedicine ?? []).map(
      (item: {
        dispensedAmount: number
        productFormId: string
        internalInventoryItemId: string
      }) => ({
        quantity: item.dispensedAmount,
        unitId: item.productFormId,
        productId: item.internalInventoryItemId
      })
    )
  })

  const validateSupplies = (values: AnyObject) => {
    let hasError = false

    values.recipesMedicine?.forEach((item: AnyObject, index: number) => {
      if (item.dispensedAmount > item.stockQuantity) {
        hasError = true
        form.setFields([
          {
            name: ['recipesMedicine', index, 'dispensedAmount'],
            errors: [t('investigations.insufficient_quantity_stock')]
          }
        ])
      }
    })

    return !hasError
  }

  const submitResults = async (testId: string, values: AnyObject) => {
    const updatedFiles = await saveFiles()

    // Only the rows on screen: values of hidden test types stay in the store.
    const shownTestTypeIds = new Set(resultTestTypes.map((item) => item.id))

    const payloads = Object.entries(values.infection ?? {})
      .filter(([key]) => shownTestTypeIds.has(key))
      .map(([key, value]: any) => ({
        ...value,
        testTypeId: key,
        resultDate: dayjs(values.resultDate).format(),
        testedBy: session?.userId,
        mediaItems: updatedFiles,
        docs: undefined
      }))

    for (const payload of payloads) {
      await createResult({ id: testId, requestBody: payload })
    }
  }

  const onSaveTestOnly = async () => {
    await form.validateFields(['testedAt', 'investigationTypeId', 'typeId'])

    if (!validateSupplies(form.getFieldsValue())) return

    setIsSaving(true)
    try {
      await createTest({ requestBody: buildTestPayload(form.getFieldsValue()) })
      message.success(t('investigations.test_created_successfully'))
      invalidateTests()
      onCancel()
    } catch (err) {
      formErrorHandler((err as ApiError).body?.errors)
    } finally {
      setIsSaving(false)
    }
  }

  const onSaveTestAndResult = async () => {
    const values = await form.validateFields()

    if (!validateSupplies(values)) return

    setIsSaving(true)
    try {
      let testId = createdTestId

      // Only create the test once, so a failed result step can be retried
      // without producing a duplicate test.
      if (!testId) {
        const { id } = await createTest({
          requestBody: buildTestPayload(values)
        })
        testId = id
        setCreatedTestId(id)
      }

      await submitResults(String(testId), values)

      message.success(t('investigations.successfully_added_result'))
      invalidateTests()
      onCancel()
    } catch (err) {
      formErrorHandler((err as ApiError).body?.errors)
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    if (template) {
      const medicines =
        template.supplies?.map((supply) => ({
          name: supply.internationalName?.name,
          internalInventoryItemId: supply.product?.id,
          productFormName: supply.unit?.name,
          productFormId: supply.unit?.id,
          dispensedAmount: supply.quantity,
          stockQuantity: supply.currentQuantity
        })) ?? []

      form.setFieldsValue({ recipesMedicine: medicines })
    }
  }, [template])

  useEffect(() => {
    form.setFieldsValue({
      testedAt: dayjs(),
      resultDate: dayjs(),
      investigationTypeId: typeId
    })
  }, [])

  // Test type, results and consumables belong to the investigation type.
  const onInvestigationTypeChange = () =>
    form.resetFields(['typeId', 'infection', RECIPE_MEDICINE_FORM])

  return (
    <Modal
      open
      centered
      width={1100}
      footer={null}
      maskClosable={false}
      title={t('analysis.add_test_and_result')}
      onCancel={onCancel}
      {...props}
    >
      <Flex vertical gap={8}>
        <Text weight={400}>
          {t('investigations.laboratory_tehnic')}{' '}
          <Text weight={700}>
            {getFullName(userData?.firstName, userData?.lastName)}
          </Text>
        </Text>

        {/* Read-only context on what is being tested: it all comes from the
            investigation type classifier, nothing is entered here. */}
        {investigationDetails.map(({ label, value }) => (
          <Text key={label} weight={400}>
            {label}: <Text weight={700}>{value}</Text>
          </Text>
        ))}
      </Flex>

      <Divider className={styles.divider} />

      <Form layout="vertical" requiredMark={false} form={form}>
        <Card variant="borderless">
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Form.Item
                name="testedAt"
                label={t('analysis.add_test.test_date')}
                rules={requiredRules}
              >
                <DatePicker
                  className="full-width"
                  format={dayMonthYearHourMinutesFormatWithDot}
                  disabledDate={disabledFutureDate}
                  showTime
                  showSecond={false}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="investigationTypeId"
                label={t('laboratory_classifiers.investigation_type')}
                rules={requiredRules}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  disabled={!!createdTestId || !canChangeInvestigationType}
                  options={investigationTypeOptions}
                  placeholder={t('laboratory_classifiers.investigation_type')}
                  onChange={onInvestigationTypeChange}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="typeId"
                label={t('specimens.test_type')}
                rules={requiredRules}
              >
                <TestTypesInfiniteSelect
                  key={selectedTypeId}
                  disabled={!!createdTestId}
                  placeholder={t('specimens.test_type')}
                  investigationTypeIds={selectedTypeId ? [selectedTypeId] : []}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Form.List name={RECIPE_MEDICINE_FORM}>
          {(fields, { remove }) => (
            <TestConsumablesTables
              loading={isLoadingTemplate}
              fields={fields}
              remove={remove}
              form={form}
            />
          )}
        </Form.List>

        <Divider className={styles.divider} />

        <Card variant="borderless" loading={isLoading}>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Form.Item
                name="resultDate"
                label={t('tests.result_datetime')}
                rules={requiredRules}
              >
                <DatePicker
                  className="full-width"
                  showTime
                  showSecond={false}
                  disabledDate={disabledFutureDate}
                  format={dayMonthYearHourMinutesFormatWithDot}
                />
              </Form.Item>
            </Col>

            {resultTestTypes.map((item) => {
              const resultOptionId =
                allValues?.infection?.[String(item.id)]?.resultOptionId

              const unitMeasure =
                item.resultUnitMeasure?.translation?.[currentLang?.value]

              return (
                <React.Fragment key={item.id}>
                  <Col span={24}>
                    <Text size="xl">
                      {t('investigations.tested_at')}:{' '}
                      {item?.infection?.translation?.[currentLang?.value]}
                    </Text>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={['infection', String(item.id), 'resultOptionId']}
                      label={t('tests_results.result')}
                      rules={requiredRules}
                    >
                      <Select
                        options={item?.resultOptions?.map((option) => ({
                          value: option.id,
                          label: option.translation?.[currentLang?.value]
                        }))}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={[
                        'infection',
                        String(item.id),
                        'resultValueOptionId'
                      ]}
                      label={t('specimens.result_option')}
                    >
                      <Select
                        options={item?.resultOptions
                          ?.find((option) => option.id === resultOptionId)
                          ?.valueOptions?.map((option) => ({
                            value: option.id,
                            label: option.translation?.[currentLang?.value]
                          }))}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      name={['infection', String(item.id), 'resultValue']}
                      label={`${t('specimens.result_value')}${
                        unitMeasure ? ` (${unitMeasure})` : ''
                      }`}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </React.Fragment>
              )
            })}

            <Col span={24}>
              <Form.Item name="docs" className="full-width">
                <Upload
                  onChange={onFileChange}
                  customRequest={() => null}
                  itemRender={(_, file, __, { remove }) => (
                    <CustomItemRender file={file} onRemove={remove} />
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Divider className={styles.divider} />

        <Flex justify="center" gap={8}>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>

          {!createdTestId && (
            <Button onClick={onSaveTestOnly} loading={isSaving}>
              {t('analysis.save_test_only')}
            </Button>
          )}

          <Button
            type="primary"
            onClick={onSaveTestAndResult}
            loading={isSaving || isUploading}
          >
            {createdTestId
              ? t('analysis.retry_save_result')
              : t('analysis.add_test_and_result')}
          </Button>
        </Flex>
      </Form>
    </Modal>
  )
}
